import React from 'react'
import { useNavigate, useParams } from 'react-router'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { FormTextInput } from '@divvy-web/skylab.form'
import { useForm } from '@divvy-web/skylab.react-hook-form'

import { useGetCreditApplicationForReviewSuspenseQuery } from '@sputnik/pages/CreditApplicationPages/ReviewForm/GetCreditApplicationForReview.gql'
import SectionCollapsibleList from '@sputnik/pages/CreditApplicationPages/ReviewForm/SectionCollapsibleList'
import {
  hasEmptyValue,
  hasMissingRequiredField,
  toSentenceCase,
} from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import styles from './styles.module.scss'

const OutsourcedAccountantReviewSection = () => {
  const navigate = useNavigate()
  // this control is just so we can not use legacy form context
  const { control } = useForm()
  const { appId } = useParams()
  const [getClassName] = useNameFormatter('OutsourcedAccountantReviewSection')
  const { track } = useWebTracking()

  const { data } = useGetCreditApplicationForReviewSuspenseQuery({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const { firstName, lastName, email } = data?.creditApplication?.outsourcedAccountant || {}

  const hasOutsourcedAccountant = !!(firstName || lastName || email)

  const hasMissingInfo = hasOutsourcedAccountant && hasMissingRequiredField([firstName, lastName, email])

  const handleOnEditClick = () => {
    track('se_signup__review_and_sign_edit_info__fired', {
      application_section: 'Outsourced accountant',
      navigated_to_page: `${PATHNAME_CONSTS.PEOPLE_INFO_PATH}`,
    })

    navigate(`../${PATHNAME_CONSTS.PEOPLE_INFO_PATH}`)
  }

  return (
    <div className={`${styles[getClassName('')]} dd-privacy-allow`}>
      <SectionCollapsibleList
        hasMissingInfo={hasMissingInfo}
        headerText={
          <FormattedMessage
            defaultMessage='Outsourced accountant'
            id='sputnik.OutsourcedAccountantReviewSection__h8O5Rm'
          />
        }
        onEditClick={handleOnEditClick}
      >
        {!hasOutsourcedAccountant && (
          <FormattedMessage
            defaultMessage='No outsourced accountant'
            id='sputnik.OutsourcedAccountantReviewSection__Ja+gAb'
          />
        )}
        {hasOutsourcedAccountant && (
          <>
            <FormTextInput
              readOnly
              className='dd-privacy-mask'
              control={control}
              isRequired={hasEmptyValue(firstName)}
              label={
                <span className='dd-privacy-allow'>
                  <FormattedMessage
                    defaultMessage='First name'
                    id='sputnik.OutsourcedAccountantReviewSection__pONqz8'
                  />
                </span>
              }
              name='outsourcedAccountantFirstName'
              value={firstName ? toSentenceCase(firstName) : '—'}
            />
            <FormTextInput
              readOnly
              className='dd-privacy-mask'
              control={control}
              isRequired={hasEmptyValue(lastName)}
              label={
                <span className='dd-privacy-allow'>
                  <FormattedMessage
                    defaultMessage='Last name'
                    id='sputnik.OutsourcedAccountantReviewSection__txUL0F'
                  />
                </span>
              }
              name='outsourcedAccountantLastName'
              value={lastName ? toSentenceCase(lastName) : '—'}
            />
            <FormTextInput
              readOnly
              className='dd-privacy-mask'
              control={control}
              isRequired={hasEmptyValue(email)}
              label={
                <span className='dd-privacy-allow'>
                  <FormattedMessage
                    defaultMessage='Business email'
                    id='sputnik.OutsourcedAccountantReviewSection__De2rFS'
                  />
                </span>
              }
              name='outsourcedAccountantEmail'
              value={email ?? '—'}
            />
          </>
        )}
      </SectionCollapsibleList>
    </div>
  )
}

export default OutsourcedAccountantReviewSection
